package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Undo
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.ui.adaptive.WideContentMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.viewmodels.PersonAccessEvent
import com.evoapps.evoshift.viewmodels.PersonAccessViewModel
import com.evoapps.evoshift.widgets.EvoDropdown
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.LoadingOverlay
import com.evoapps.evoshift.widgets.SurfaceCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.access_above_own
import evoshift.composeapp.generated.resources.access_intro
import evoshift.composeapp.generated.resources.access_modified
import evoshift.composeapp.generated.resources.access_older_apps
import evoshift.composeapp.generated.resources.access_reset_feature
import evoshift.composeapp.generated.resources.access_reset_to_role
import evoshift.composeapp.generated.resources.access_role
import evoshift.composeapp.generated.resources.access_title
import evoshift.composeapp.generated.resources.save
import org.jetbrains.compose.resources.stringResource

/*
 * SH-270: "Uprawnienia" of one person at a workplace, from their card in the Team. A role (a
 * ready set of levels) and, per feature, none / view / edit: a level that differs from the role's
 * is a modification of this person ("Zmienione", with its own reset), "Przywróć uprawnienia roli"
 * drops them all. Levels above the viewer's own are not offered (the server refuses them too).
 */

/** Test tag of a feature's level choice (by feature id and level). */
fun featureLevelTag(feature: Feature, level: AccessLevel) = "feature_level_${feature.id}_${level.name}"

/** Test tag of a feature's "Reset to the role's level". */
fun featureResetTag(feature: Feature) = "feature_reset_${feature.id}"

@Composable
fun PersonAccessScreen(
    viewModel: PersonAccessViewModel,
    onNavigateBack: () -> Unit
) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val errorText = state.error?.asString()
    val successText = state.successMessage?.asString()
    LaunchedEffect(errorText, successText) {
        (errorText ?: successText)?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.onEvent(PersonAccessEvent.MessageShown)
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = { EvoTopBar(title = stringResource(Res.string.access_title), onBack = onNavigateBack) }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize().contentWidth(WideContentMaxWidth)) {
            Column(
                Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Text(state.personName, style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Bold)
                val blocked = state.blocked?.asString()
                Text(
                    blocked ?: stringResource(Res.string.access_intro),
                    style = MaterialTheme.typography.bodyMedium,
                    color = if (blocked != null) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurfaceVariant
                )
                if (!state.isLoading) {
                    EvoDropdown(
                        label = stringResource(Res.string.access_role),
                        options = state.choices,
                        selected = state.role,
                        onSelected = { viewModel.onEvent(PersonAccessEvent.SelectRole(it.id)) },
                        optionLabel = { it.label() },
                        enabled = state.canEdit && !state.isSaving,
                        onLight = true
                    )
                    FeatureLevelsTable(
                        levels = state.levels,
                        modified = state.modified,
                        enabled = state.canEdit && !state.isSaving,
                        allows = state::allows,
                        onLevel = { feature, level -> viewModel.onEvent(PersonAccessEvent.SetLevel(feature, level)) },
                        onReset = { viewModel.onEvent(PersonAccessEvent.ResetFeature(it)) }
                    )
                    if (state.aboveOwn.isNotEmpty()) {
                        Text(
                            stringResource(Res.string.access_above_own, state.aboveOwn.map { it.label() }.joinToString(", ")),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                    Text(
                        stringResource(Res.string.access_older_apps, state.permission.label()),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    if (state.canEdit) {
                        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                            TextButton(
                                onClick = { viewModel.onEvent(PersonAccessEvent.ResetToRole) },
                                enabled = state.modified.isNotEmpty() && !state.isSaving
                            ) { Text(stringResource(Res.string.access_reset_to_role)) }
                            Button(onClick = { viewModel.onEvent(PersonAccessEvent.Save) }, enabled = state.canSave) {
                                Text(stringResource(Res.string.save))
                            }
                        }
                    }
                }
                Spacer(Modifier.height(48.dp))
            }
            LoadingOverlay(isLoading = state.isLoading)
        }
    }
}

/**
 * Every feature with its level (none / view / edit). [modified]: the features that differ from the
 * role, marked and with a reset ([onReset] null: no role to go back to, e.g. the role editor).
 * [allows]: whether a level may be chosen (the viewer's own access).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun FeatureLevelsTable(
    levels: Map<Feature, AccessLevel>,
    enabled: Boolean,
    onLevel: (Feature, AccessLevel) -> Unit,
    modifier: Modifier = Modifier,
    modified: Set<Feature> = emptySet(),
    allows: (Feature, AccessLevel) -> Boolean = { _, _ -> true },
    onReset: ((Feature) -> Unit)? = null
) {
    SurfaceCard(modifier.fillMaxWidth()) {
        Column(Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Feature.entries.forEach { feature ->
                val level = levels[feature] ?: AccessLevel.NONE
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Text(feature.label(), style = MaterialTheme.typography.titleSmall, modifier = Modifier.weight(1f))
                        if (feature in modified) {
                            Text(
                                stringResource(Res.string.access_modified),
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.primary
                            )
                            if (onReset != null) {
                                IconButton(
                                    onClick = { onReset(feature) },
                                    enabled = enabled,
                                    modifier = Modifier.testTag(featureResetTag(feature))
                                ) {
                                    Icon(Icons.AutoMirrored.Filled.Undo, contentDescription = stringResource(Res.string.access_reset_feature))
                                }
                            }
                        }
                    }
                    SingleChoiceSegmentedButtonRow(Modifier.fillMaxWidth()) {
                        AccessLevel.entries.forEachIndexed { index, option ->
                            SegmentedButton(
                                selected = option == level,
                                onClick = { onLevel(feature, option) },
                                enabled = enabled && allows(feature, option),
                                shape = SegmentedButtonDefaults.itemShape(index = index, count = AccessLevel.entries.size),
                                modifier = Modifier.testTag(featureLevelTag(feature, option))
                            ) { Text(option.label()) }
                        }
                    }
                }
            }
        }
    }
}
